import type { ReactNode } from 'react'
import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/_ui/Dialog'
import T from '@/components/Helpers/Translate'
import Image from 'next/image'
import { useMediaQuery } from 'react-responsive'
import { Drawer, DrawerContent } from '@/components/_ui/Drawer'
import { useModals } from '../useModals'

export type ModalsManagerDialogOrDrawerProps = {
  title?: string
  description?: string
  isOpen?: boolean
  children: ReactNode
  allowClickOutside?: boolean
  onClose?: () => void
  dismissible?: boolean
  modalId?: string
  titleIcon?: string
  titleStyles?: string
  dialogProps?: DialogProps
  zIndex?: number
}

type DialogProps = {
  className?: string
}

export const ModalsManagerDialogOrDrawer = (
  props: ModalsManagerDialogOrDrawerProps,
) => {
  const {
    isOpen = true,
    allowClickOutside = true,
    zIndex = 1000,
    modalId,
    titleIcon,
    titleStyles,
    title,
    description,
    children,
    dialogProps,
    onClose,
    dismissible = true,
  } = props

  const { modals } = useModals()
  const isDesktop = useMediaQuery({ minWidth: 1024 })

  const handleCloseModal = ({ open }: { open: boolean }) => {
    if (!open && modalId) {
      modals.updateModal(modalId, { isOpen: false })
      return
    }

    if (!open) {
      modals.closeModals()
    }
  }

  if (!isOpen) return null

  if (!isDesktop) {
    return (
      <Drawer
        dismissible={dismissible}
        onClose={() => {
          if (onClose) {
            onClose()
          }
        }}
        direction="bottom"
        open={isOpen}
        onOpenChange={(value) => {
          handleCloseModal({ open: value })
        }}
      >
        <DrawerContent
          zIndex={zIndex}
          className="max-h-[90vh] w-full overflow-hidden px-0 sm:px-5"
        >
          {children}
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(value) => {
        if (!dismissible) return

        if (value === false && onClose) {
          onClose()
        }

        handleCloseModal({ open: value })
      }}
    >
      <DialogContent
        zIndex={zIndex}
        style={{ maxHeight: '80vh', maxWidth: '90vh' }}
        onInteractOutside={(event) => {
          if (!allowClickOutside) event.preventDefault()
        }}
        className={dialogProps?.className}
      >
        <DialogHeader>
          {titleIcon && (
            <Image
              src={titleIcon}
              alt={title || 'info_icon'}
              height={18}
              width={29}
            />
          )}
          {title && (
            <DialogTitle className={titleStyles}>
              <T>{title}</T>
            </DialogTitle>
          )}
          {description && (
            <DialogDescription>
              <T>{description}</T>
            </DialogDescription>
          )}
        </DialogHeader>

        {children}
      </DialogContent>
    </Dialog>
  )
}
